import { useEffect, useRef, useState } from "react";
import { ArrowDownLeft, ArrowUpRight, FileText, UploadCloud, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { useInvoices } from "../../lib/invoices/useInvoices";
import { trackEvent } from "../../lib/telemetry/analytics";
import type {
  InvoiceDirection,
  InvoiceStatus,
  NewInvoiceInput,
} from "../../lib/invoices/types";

interface NewInvoiceDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  initialValues?: Partial<NewInvoiceInput>;
  mode?: "create" | "edit";
  invoiceId?: string;
}

const ACCEPT = "image/*,application/pdf";

// Parse a user-entered dollar amount ("129.00", "$1,234.56", "5") into cents.
// Returns null if invalid.
function parseAmountToCents(input: string): number | null {
  const trimmed = input.trim().replace(/[$,\s]/g, "");
  if (trimmed === "") return null;
  if (!/^\d+(\.\d{0,2})?$/.test(trimmed)) return null;
  const asFloat = Number.parseFloat(trimmed);
  if (!Number.isFinite(asFloat)) return null;
  return Math.round(asFloat * 100);
}

function centsToInput(cents: number | undefined): string {
  if (cents === undefined || cents === null) return "";
  return (cents / 100).toFixed(2);
}

const STATUS_OPTIONS: { value: "" | InvoiceStatus; label: string }[] = [
  { value: "", label: "Auto (derive from due date)" },
  { value: "draft", label: "Draft" },
  { value: "pending", label: "Pending" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
  { value: "cancelled", label: "Cancelled" },
];

export function NewInvoiceDialog({
  open,
  onOpenChange,
  initialValues,
  mode = "create",
  invoiceId,
}: NewInvoiceDialogProps) {
  const { add, update } = useInvoices();

  const [counterparty, setCounterparty] = useState("");
  const [direction, setDirection] = useState<InvoiceDirection>("incoming");
  const [amountText, setAmountText] = useState("");
  const [issuedOn, setIssuedOn] = useState("");
  const [dueOn, setDueOn] = useState("");
  const [statusSel, setStatusSel] = useState<"" | InvoiceStatus>("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setError(null);
      setSubmitting(false);
      setDragActive(false);
      return;
    }
    setCounterparty(initialValues?.counterparty ?? "");
    setDirection(initialValues?.direction ?? "incoming");
    setAmountText(
      initialValues?.amountCents !== undefined
        ? centsToInput(initialValues.amountCents)
        : "",
    );
    setIssuedOn(initialValues?.issuedOn ?? "");
    setDueOn(initialValues?.dueOn ?? "");
    setStatusSel(initialValues?.status ?? "");
    setNotes(initialValues?.notes ?? "");
    setFile(null);
    setPreview(null);
    setError(null);
    setSubmitting(false);
    setDragActive(false);
  }, [open, initialValues]);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    if (!file.type.startsWith("image/")) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  function handleFiles(files: FileList | null) {
    setError(null);
    if (!files || files.length === 0) return;
    const picked = files[0];
    if (!picked) return;
    const isImage = picked.type.startsWith("image/");
    const isPdf = picked.type === "application/pdf";
    if (!isImage && !isPdf) {
      setError("Unsupported file type. Please upload an image or PDF.");
      return;
    }
    setFile(picked);
  }

  async function handleSubmit() {
    setError(null);

    if (!counterparty.trim()) {
      setError("Counterparty is required.");
      return;
    }
    if (!issuedOn) {
      setError("Issued on is required.");
      return;
    }
    const cents = parseAmountToCents(amountText);
    if (cents === null || cents < 0) {
      setError("Amount must be a positive number (e.g. 129.00).");
      return;
    }

    setSubmitting(true);
    try {
      if (mode === "edit" && invoiceId) {
        await update(invoiceId, {
          counterparty: counterparty.trim(),
          direction,
          amountCents: cents,
          issuedOn,
          dueOn: dueOn || null,
          // When editing, empty "Auto" is interpreted as "don't touch status".
          ...(statusSel ? { status: statusSel } : {}),
          notes: notes.trim() || null,
        });
      } else {
        await add({
          counterparty: counterparty.trim(),
          direction,
          amountCents: cents,
          issuedOn,
          dueOn: dueOn || null,
          status: statusSel || undefined,
          notes: notes.trim() || undefined,
          file: file ?? undefined,
        });
        trackEvent("invoice_created", {
          direction,
          has_file: Boolean(file),
        });
      }
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Submit failed";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  const isImage = file?.type.startsWith("image/") ?? false;
  const isPdf = file?.type === "application/pdf";

  const title = mode === "edit" ? "Edit invoice" : "New invoice";
  const description =
    mode === "edit"
      ? "Update the fields below. Attach proof documents from the detail panel."
      : "Enter invoice details manually. A proof document is optional.";
  const submitLabel = mode === "edit" ? "Save changes" : "Create invoice";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="new-invoice-dialog"
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="new-invoice-counterparty"
              className="text-xs font-medium text-slate-700"
            >
              Counterparty
            </label>
            <input
              id="new-invoice-counterparty"
              data-testid="new-invoice-counterparty"
              type="text"
              value={counterparty}
              onChange={(e) => setCounterparty(e.target.value)}
              placeholder="e.g. Acme Corp"
              className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-slate-700">Direction</span>
            {/*
              A segmented radio — not a native <select> — because direction
              has only two choices and it's the pivotal filter on the hub.
              Buttons keep it one-click and keyboard-navigable via the native
              radio inputs behind them.
            */}
            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1 self-start">
              <button
                type="button"
                data-testid="new-invoice-direction-incoming"
                onClick={() => setDirection("incoming")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  direction === "incoming"
                    ? "bg-white text-emerald-700 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <ArrowDownLeft className="w-3.5 h-3.5" /> Incoming
              </button>
              <button
                type="button"
                data-testid="new-invoice-direction-outgoing"
                onClick={() => setDirection("outgoing")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  direction === "outgoing"
                    ? "bg-white text-indigo-700 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <ArrowUpRight className="w-3.5 h-3.5" /> Outgoing
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="new-invoice-amount"
                className="text-xs font-medium text-slate-700"
              >
                Amount (USD)
              </label>
              <input
                id="new-invoice-amount"
                data-testid="new-invoice-amount"
                type="text"
                inputMode="decimal"
                value={amountText}
                onChange={(e) => setAmountText(e.target.value)}
                placeholder="129.00"
                className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="new-invoice-status"
                className="text-xs font-medium text-slate-700"
              >
                Status
              </label>
              {/*
                Using a native <select> rather than the Radix Select:
                the Radix version portals its content outside the dialog,
                which complicates Playwright selectors keyed by data-testid.
                Native selects keep the form trivially testable.
              */}
              <select
                id="new-invoice-status"
                data-testid="new-invoice-status"
                value={statusSel}
                onChange={(e) =>
                  setStatusSel(e.target.value as "" | InvoiceStatus)
                }
                className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="new-invoice-issued-on"
                className="text-xs font-medium text-slate-700"
              >
                Issued on
              </label>
              <input
                id="new-invoice-issued-on"
                data-testid="new-invoice-issued-on"
                type="date"
                value={issuedOn}
                onChange={(e) => setIssuedOn(e.target.value)}
                className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="new-invoice-due-on"
                className="text-xs font-medium text-slate-700"
              >
                Due on (optional)
              </label>
              <input
                id="new-invoice-due-on"
                data-testid="new-invoice-due-on"
                type="date"
                value={dueOn}
                onChange={(e) => setDueOn(e.target.value)}
                className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2]"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="new-invoice-notes"
              className="text-xs font-medium text-slate-700"
            >
              Notes
            </label>
            <textarea
              id="new-invoice-notes"
              data-testid="new-invoice-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional — e.g. Q1 consulting retainer"
              className="px-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30 focus:border-[#0070F2] resize-none"
            />
          </div>

          {mode === "create" && (
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-slate-700">
                Proof document (optional)
              </span>
              <label
                onDragEnter={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                  handleFiles(e.dataTransfer.files);
                }}
                className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-4 text-center cursor-pointer transition-colors ${
                  dragActive
                    ? "border-blue-400 bg-blue-50"
                    : "border-slate-200 bg-slate-50 hover:border-slate-300"
                }`}
              >
                <UploadCloud className="w-6 h-6 text-slate-400" />
                <div className="text-xs text-slate-700 font-medium">
                  Drag & drop a file here, or click to browse
                </div>
                <div className="text-[11px] text-slate-500">
                  Images (PNG, JPG) or PDF
                </div>
                <input
                  ref={inputRef}
                  data-testid="new-invoice-file"
                  type="file"
                  accept={ACCEPT}
                  className="sr-only"
                  onChange={(e) => handleFiles(e.target.files)}
                />
              </label>

              {file && (
                <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
                  <div className="w-12 h-12 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                    {isImage && preview ? (
                      <img
                        src={preview}
                        alt="Selected proof preview"
                        className="object-cover w-full h-full"
                      />
                    ) : (
                      <FileText className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      data-testid="new-invoice-filename"
                      className="text-sm font-medium text-slate-900 truncate"
                    >
                      {file.name}
                    </div>
                    <div className="text-xs text-slate-500">
                      {isPdf ? "PDF" : "Image"} ·{" "}
                      {(file.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md"
                    aria-label="Clear selected file"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}

          {error && (
            <div
              data-testid="new-invoice-error"
              className="text-xs font-medium text-red-600"
            >
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="new-invoice-submit"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-[#0070F2] hover:bg-[#005BCA] disabled:bg-slate-300 disabled:cursor-not-allowed rounded-md transition-colors"
          >
            {submitting ? "Saving…" : submitLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
